// @givmo/board — a meeting's History, at the foot of its Overview (CBG-CAL1 slice 1d; question 4;
// the approved frames `meeting-history`, `meeting-sent`, `meeting-after-start`, `meeting-restored`).
//
// The meeting's own event log (`GET …/meetings/{id}/meeting-events`), newest first, where each change
// was made and never who (D-CBG-15): a change made in a calendar says WHERE, as the backend writes it
// from `origin` / `origin_provider`. Each line is the backend's own note, verbatim, except two of
// Board's own, which read as the drawn words with the backend's notice sentence behind the glyph.
//
// ══ WHEN IT SHOWS, AND WHICH LINES (the orchestrator's C1, 2026-10-07) ═══════════════════════════
//
// The log is served today, calendar or not, and it holds every Board act (an agenda item added, a
// reply recorded, a packet published, a minutes reminder). The card is the calendar's:
//   * it renders only when the calendar is in play for this meeting — the meeting read carries a
//     `calendar` block, or the log holds a calendar type or a calendar-origin event — so with the
//     calendar flags off the page is today's;
//   * it lists Scheduled, Board's own reschedules, the calendar's types and every change that came
//     from a calendar. Agenda, replies, packets, minutes, Board's note-less edits and Board's own
//     cancellation (the page's Cancelled row says its reason) stay on their own cards.
//
// An officer's "I've told everyone" (CBG-FIX46, `directors_told`) is one of the calendar's types:
// Jonathan's ruling is that the press leaves a line saying who pressed it and when. The backend
// composes the line when the log is read, with the officer's name or none, and the card shows it as
// served (the date is the row's own column).
//
// A time change adopted from a calendar reads as drawn, "Time changed in Google Calendar on Oct 3"
// (question 9): the day is the calendar's own instant, served as `origin_recorded_at` (CBG-FIX32),
// while the date column is the day Board saw it, and the backend's whole sentence sits behind the
// glyph. Until a backend serves that field the line reads "Time changed in Google Calendar" (A3);
// nothing here ever parses the day out of the sentence.
//
// Every day on the card reads in the MEETING's own zone (CBG-FIX37), the zone Board counts notice in
// and the backend's note names both its days in, so a line and the note behind its glyph never name
// different days, whatever the reader's zone.

import { Card } from '@givmo/design-system';
import { InfoTip } from '../../components/fine-print';
import { useBoardHost } from '../../board-host';
import { useMeetingEvents } from '../../calendar/calendar-queries';
import { calendarWords as w, providerName } from '../../calendar/calendar-words';
import {
  isCalendarEvent,
  type MeetingCalendar,
  type MeetingEvent,
} from '../meetings/calendar-decode';
import { meetingShortDate } from '../meetings/meetings-decode';

/** The shipped notice glyph's name, for the two lines that carry the notice sentence. */
const NOTICE_INFO = 'How the notice count is worked out';

/**
 * One history line: what it reads, and what sits behind its glyph. Null: not this card's. Its day is
 * the meeting's zone's (`timeZone`, CBG-FIX37).
 */
export function historyLine(
  event: MeetingEvent,
  timeZone?: string | null,
): { line: string; tip: string | null; tipLabel: string } | null {
  const note = event.note;
  if (event.event_type === 'scheduled') {
    return { line: w.historyScheduled, tip: note, tipLabel: NOTICE_INFO };
  }
  if (event.event_type === 'rescheduled' && event.origin === 'calendar') {
    const provider = providerName(event.origin_provider);
    return {
      line: event.origin_recorded_at
        ? w.timeChangedOn(provider, meetingShortDate(event.origin_recorded_at, timeZone))
        : w.timeChangedIn(provider),
      tip: note,
      tipLabel: w.historyNoteInfo,
    };
  }
  if (event.event_type === 'rescheduled' && event.origin === 'board') {
    return { line: w.historyRescheduled, tip: note, tipLabel: NOTICE_INFO };
  }
  if (isCalendarEvent(event) && note !== null) {
    return { line: note, tip: null, tipLabel: '' };
  }
  return null;
}

/** Where the meeting's last reschedule was made, when a calendar made it (the Rescheduled row). */
export function lastRescheduleProvider(events: readonly MeetingEvent[]): string | null {
  const last = [...events].reverse().find((event) => event.event_type === 'rescheduled');
  return last?.origin === 'calendar' ? providerName(last.origin_provider) : null;
}

export function MeetingHistoryCard({
  meetingId,
  calendar,
  timeZone,
}: {
  meetingId: string;
  calendar: MeetingCalendar | null;
  /** The meeting's own zone: every day on the card reads in it (CBG-FIX37). */
  timeZone: string | null | undefined;
}) {
  const { orgRef } = useBoardHost();
  const events = useMeetingEvents(orgRef, meetingId);
  const log = events.data ?? null;
  if (log === null) return null;
  const inPlay = calendar !== null || log.some(isCalendarEvent);
  if (!inPlay) return null;
  const lines = [...log]
    .reverse()
    .map((event) => ({ event, shown: historyLine(event, timeZone) }))
    .filter(
      (
        entry,
      ): entry is { event: MeetingEvent; shown: NonNullable<ReturnType<typeof historyLine>> } =>
        entry.shown !== null,
    );
  if (lines.length === 0) return null;

  return (
    <Card testId="meeting-history">
      <div className="border-b border-border-subtle px-20 py-16">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {w.historyTitle}
        </h2>
      </div>
      <ol className="divide-y divide-border-subtle">
        {lines.map(({ event, shown }) => (
          <li
            key={event.id}
            className="grid grid-cols-[56px_minmax(0,1fr)] gap-12 px-20 py-12 sm:grid-cols-[72px_minmax(0,1fr)] sm:gap-16"
            data-testid={`meeting-history-${event.id}`}
          >
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {meetingShortDate(event.occurred_at, timeZone)}
            </span>
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text">
              {shown.line}
              {shown.tip !== null ? (
                <>
                  {' '}
                  <InfoTip label={shown.tipLabel} testId={`meeting-history-${event.id}-info`}>
                    <p>{shown.tip}</p>
                  </InfoTip>
                </>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}
